import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiX, FiSearch, FiPlus, FiUserPlus } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { searchPlayers } from '../actions/playersActions';
import EditPlayerModal from './EditPlayerModal';
import '../styles/addPlayerModal.css';

export default function AddPlayerModal({ 
    isOpen, 
    onClose, 
    onSuccess, 
    applicationId, 
    application,
    team,
    teamId,
    occupiedNumbers = [] 
}) {
    const dispatch = useDispatch();
    const userState = useSelector(state => state.user);
    const user = userState?.userData?.data || {};
    
    // Режим: 'single' или 'multiple'
    const [mode, setMode] = useState('single');
    
    // Для режима "один игрок"
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [playerNumber, setPlayerNumber] = useState('');
    const [showCreatePlayerModal, setShowCreatePlayerModal] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    
    // Для режима "несколько игроков"
    const [availableApplications, setAvailableApplications] = useState([]);
    const [selectedSourceApplication, setSelectedSourceApplication] = useState(null);
    const [sourceApplicationPlayers, setSourceApplicationPlayers] = useState([]);
    const [selectedPlayers, setSelectedPlayers] = useState([]);
    const [isLoadingApplications, setIsLoadingApplications] = useState(false);
    const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
    const [sortBy, setSortBy] = useState('family'); // 'family' или 'number'

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            alert('Введите фамилию игрока для поиска');
            return;
        }
        
        if (searchQuery.trim().length < 2) {
            alert('Введите минимум 2 символа для поиска');
            return;
        }

        try {
            setIsSearching(true);
            
            // Разделяем запрос на фамилию и имя по пробелу
            const searchParts = searchQuery.trim().split(/\s+/);
            const familyQuery = searchParts[0] || '';
            const nameQuery = searchParts[1] || '';
            
            // Используем готовое действие из Redux
            const result = await dispatch(searchPlayers(
                API_URLS.searchPlayersByParams,
                {
                    family: familyQuery,
                    name: nameQuery,
                    father_name: ''
                },
                0, // offset
                100 // limit
            ));
            
            // Проверяем различные форматы ответа
            let players = [];
            if (result && result.data) {
                if (Array.isArray(result.data.players)) {
                    players = result.data.players;
                } else if (Array.isArray(result.data.data)) {
                    players = result.data.data;
                } else if (Array.isArray(result.data)) {
                    players = result.data;
                }
            }
            
            setSearchResults(players);
            setHasSearched(true); // Отмечаем, что поиск был выполнен
        } catch (error) {
            alert('Ошибка при поиске игроков: ' + (error?.response?.data?.message || error?.message || 'Неизвестная ошибка'));
            setSearchResults([]);
            setHasSearched(true);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelectPlayer = (player) => {
        setSelectedPlayer(player);
        setPlayerNumber('');
    };

    const handleAddPlayer = async () => {
        if (!applicationId) {
            alert('Ошибка: ID заявки не определен');
            return;
        }

        if (!selectedPlayer) {
            alert('Выберите игрока');
            return;
        }

        if (!playerNumber || playerNumber < 1 || playerNumber > 99) {
            alert('Введите игровой номер от 1 до 99');
            return;
        }

        try {
            setIsAdding(true);
            
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }
            
            const tokens = getTokensFromStore();
            
            // Проверяем актуальный список занятых номеров перед добавлением
            try {
                const applicationsResponse = await axios.get(
                    BASE_URL.url + `/teams/${teamId}/applicationsWithActiveProfiles`,
                    {
                        headers: {
                            "Authorization": "Bearer " + tokens.access_token,
                            'Content-Type': 'application/json',
                        }
                    }
                );
                
                // Находим текущую заявку и получаем список занятых номеров
                let currentOccupiedNumbers = [];
                if (Array.isArray(applicationsResponse.data)) {
                    const currentApp = applicationsResponse.data.find(app => app.application_id === applicationId);
                    if (currentApp && currentApp.data) {
                        currentOccupiedNumbers = currentApp.data
                            .map(p => p.player_number || p.number || p.playerNumber)
                            .filter(n => n);
                    }
                } else if (applicationsResponse.data && applicationsResponse.data.rosters) {
                    const currentApp = applicationsResponse.data.rosters.find(app => app.application_id === applicationId);
                    if (currentApp && currentApp.data) {
                        currentOccupiedNumbers = currentApp.data
                            .map(p => p.player_number || p.number || p.playerNumber)
                            .filter(n => n);
                    }
                }
                
                // Проверяем, занят ли выбранный номер
                if (currentOccupiedNumbers.includes(parseInt(playerNumber))) {
                    alert(`Номер ${playerNumber} уже занят другим игроком в этой заявке!`);
                    setIsAdding(false);
                    return;
                }
            } catch (checkError) {
                console.error('Error checking occupied numbers:', checkError);
                // Если не удалось проверить, используем старые данные
                if (occupiedNumbers && occupiedNumbers.includes(parseInt(playerNumber))) {
                    alert(`Номер ${playerNumber} уже занят другим игроком`);
                    setIsAdding(false);
                    return;
                }
            }
            
            // Формируем объект команды по аналогии с React Native приложением
            // В RN приложении передается полный объект команды с дополнительными полями из заявки
            const teamData = {
                ...team, // все поля команды (id, name, client_id и т.д.)
                player_number: parseInt(playerNumber),
                application_id: applicationId,
                application_name: application?.application_name || application?.name || '',
                application_comment: application?.application_comment || application?.comment || '',
                application_year: application?.year || application?.application_year || new Date().getFullYear()
            };
            
            
            // Используем формат из React Native приложения
            const requestData = {
                playerId: selectedPlayer.id || selectedPlayer.player_id,
                add_data: [teamData], // передаем полный объект команды с доп. полями
                remove_data: []
            };
            
            console.log('Adding player to application:', requestData);
            console.log('Selected player:', selectedPlayer);
            
            const response = await axios.post(
                BASE_URL.url + '/players/connectToApplication',
                requestData,
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            if (response.data.success || response.status === 200 || response.status === 201) {
                alert('Игрок успешно дозаявлен!');
                setSearchQuery('');
                setSearchResults([]);
                setSelectedPlayer(null);
                setPlayerNumber('');
                onSuccess();
                onClose();
            } else {
                throw new Error('Неожиданный ответ сервера');
            }
        } catch (error) {
            let errorMessage = 'Произошла ошибка при дозаявке игрока';
            
            if (error.response) {
                errorMessage = error.response?.data?.message 
                    || error.response?.data?.error 
                    || JSON.stringify(error.response?.data)
                    || `Ошибка сервера: ${error.response.status}`;
            } else if (error.request) {
                errorMessage = 'Нет ответа от сервера';
            } else {
                errorMessage = error.message;
            }
            
            alert('Ошибка при дозаявке игрока:\n' + errorMessage);
        } finally {
            setIsAdding(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };
    
    // Автоматический поиск при вводе 4+ символов с debounce
    useEffect(() => {
        if (searchQuery.trim().length >= 4 && !isSearching) {
            const timeoutId = setTimeout(() => {
                handleSearch();
            }, 800); // Задержка 800мс для debounce
            
            return () => clearTimeout(timeoutId); // Очистка таймера при изменении
        }
    }, [searchQuery]);
    
    const handleSearchQueryChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
    };

    const handleClose = () => {
        // Сброс для режима "один игрок"
        setSearchQuery('');
        setSearchResults([]);
        setSelectedPlayer(null);
        setPlayerNumber('');
        setHasSearched(false);
        
        // Сброс для режима "несколько игроков"
        setMode('single');
        setSelectedSourceApplication(null);
        setSourceApplicationPlayers([]);
        setSelectedPlayers([]);
        
        onClose();
    };

    const handleBackToSearch = () => {
        setSelectedPlayer(null);
        setPlayerNumber('');
        setSearchResults([]);
        setHasSearched(false);
    };
    
    // Функции для режима "несколько игроков"
    useEffect(() => {
        if (isOpen && mode === 'multiple') {
            loadAvailableApplications();
        }
    }, [isOpen, mode]);
    
    const loadAvailableApplications = async () => {
        try {
            setIsLoadingApplications(true);
            
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }
            
            const tokens = getTokensFromStore();
            
            // Загружаем заявки с игроками
            const appsWithPlayersResponse = await axios.get(
                BASE_URL.url + `/teams/${teamId}/applicationsWithActiveProfiles`,
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            let appsWithPlayers = [];
            if (Array.isArray(appsWithPlayersResponse.data)) {
                appsWithPlayers = appsWithPlayersResponse.data;
            } else if (appsWithPlayersResponse.data?.rosters) {
                appsWithPlayers = appsWithPlayersResponse.data.rosters;
            }
            
            // Загружаем ВСЕ заявки команды (включая пустые)
            const allAppsResponse = await axios.post(
                BASE_URL.url + '/teams/getAdminApplications',
                { team_id: teamId },
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            let allApps = [];
            if (Array.isArray(allAppsResponse.data)) {
                allApps = allAppsResponse.data;
            } else if (allAppsResponse.data?.applications) {
                allApps = allAppsResponse.data.applications;
            } else if (allAppsResponse.data?.data) {
                allApps = allAppsResponse.data.data;
            }
            
            // Создаём Map для быстрого поиска по application_id
            const appsMap = new Map();
            
            // Сначала добавляем все заявки с игроками
            appsWithPlayers.forEach(app => {
                const appId = app.application_id || app.id;
                appsMap.set(appId, app);
            });
            
            // Затем добавляем заявки без игроков (если их ещё нет в Map)
            allApps.forEach(app => {
                const appId = app.application_id || app.id;
                if (!appsMap.has(appId)) {
                    appsMap.set(appId, {
                        ...app,
                        data: []
                    });
                }
            });
            
            // Преобразуем Map в массив
            const mergedApplications = Array.from(appsMap.values());
            
            // Фильтруем текущую заявку
            const filteredApplications = mergedApplications.filter(app => {
                const appId = app.application_id || app.id;
                return appId !== applicationId;
            });
            
            setAvailableApplications(filteredApplications);
        } catch (error) {
            console.error('Error loading applications:', error);
            alert('Ошибка при загрузке заявок');
        } finally {
            setIsLoadingApplications(false);
        }
    };
    
    const handleSelectSourceApplication = async (appId) => {
        const app = availableApplications.find(a => a.application_id === appId);
        setSelectedSourceApplication(app);
        setSourceApplicationPlayers(app?.data || []);
        setSelectedPlayers([]);
    };
    
    const handleTogglePlayer = (player) => {
        setSelectedPlayers(prev => {
            const isSelected = prev.some(p => (p.id || p.player_id) === (player.id || player.player_id));
            if (isSelected) {
                return prev.filter(p => (p.id || p.player_id) !== (player.id || player.player_id));
            } else {
                return [...prev, player];
            }
        });
    };
    
    const handleToggleAll = () => {
        if (selectedPlayers.length === sourceApplicationPlayers.length) {
            setSelectedPlayers([]);
        } else {
            setSelectedPlayers([...sourceApplicationPlayers]);
        }
    };
    
    // Функция сортировки игроков
    const getSortedPlayers = (players) => {
        if (!players || players.length === 0) return [];
        
        const sorted = [...players];
        
        if (sortBy === 'number') {
            sorted.sort((a, b) => {
                const numA = a.player_number || a.number || 999;
                const numB = b.player_number || b.number || 999;
                return numA - numB;
            });
        } else {
            // Сортировка по ФИО
            sorted.sort((a, b) => {
                const nameA = `${a.family || ''} ${a.name || ''}`.toLowerCase();
                const nameB = `${b.family || ''} ${b.name || ''}`.toLowerCase();
                return nameA.localeCompare(nameB, 'ru');
            });
        }
        
        return sorted;
    };
    
    const handleAddMultiplePlayers = async () => {
        if (selectedPlayers.length === 0) {
            alert('Выберите хотя бы одного игрока');
            return;
        }
        
        try {
            setIsAdding(true);
            
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }
            
            const tokens = getTokensFromStore();

            const players = [];
            for (const player of selectedPlayers) {
                const rawNumber = player.player_number || player.number;
                const playerNumber = rawNumber ? parseInt(rawNumber, 10) : null;

                if (playerNumber !== null && (isNaN(playerNumber) || playerNumber < 0 || playerNumber > 99)) {
                    console.error('Некорректный номер игрока:', rawNumber);
                    continue;
                }

                const teamData = {
                    ...team,
                    player_number: playerNumber,
                    application_id: applicationId,
                    application_name: application?.application_name || application?.name || '',
                    application_comment: application?.application_comment || application?.comment || '',
                    application_year: application?.year || application?.application_year || new Date().getFullYear()
                };

                players.push({
                    playerId: player.id || player.player_id,
                    add_data: [teamData],
                    remove_data: []
                });
            }

            if (players.length === 0) {
                alert('Нет игроков с корректными данными для добавления');
                return;
            }

            const response = await axios.post(
                BASE_URL.url + API_URLS.connectManyToApplication,
                { players },
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                }
            );

            const rows = Array.isArray(response.data) ? response.data
                : Array.isArray(response.data?.data) ? response.data.data : [];
            const failed = rows.filter(r => r && r.error);
            const added = players.length - failed.length;

            if (failed.length === 0) {
                alert(`Успешно добавлено игроков: ${added}`);
                handleClose();
                onSuccess();
            } else if (added > 0) {
                alert(`Добавлено: ${added}. Не удалось: ${failed.length}.`
                    + (failed[0].error ? `\n${failed[0].error}` : ''));
                onSuccess();
            } else {
                alert('Ошибка при добавлении игроков'
                    + (failed[0].error ? `: ${failed[0].error}` : ''));
            }
        } catch (error) {
            console.error('Error adding players:', error);
            alert('Ошибка при добавлении игроков');
        } finally {
            setIsAdding(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modalOverlay">
            <div className="addPlayerModalContent">
                <div className="addPlayerModalHeader">
                    <h2>Дозаявить игрока</h2>
                    <button className="closeButton" onClick={handleClose}>
                        <FiX />
                    </button>
                </div>

                <div className="addPlayerModalBody">
                    {/* Переключатель режима */}
                    <div className="modeSelector">
                        <button 
                            className={`modeButton ${mode === 'single' ? 'active' : ''}`}
                            onClick={() => setMode('single')}
                        >
                            Один игрок
                        </button>
                        <button 
                            className={`modeButton ${mode === 'multiple' ? 'active' : ''}`}
                            onClick={() => setMode('multiple')}
                        >
                            Несколько игроков
                        </button>
                    </div>

                    {mode === 'single' ? (
                        // Режим "один игрок" - существующий функционал
                        !selectedPlayer ? (
                        <>
                            <div className="searchSection">
                                <label>Поиск игрока</label>
                                <div className="searchInputWrapper">
                                    <input
                                        type="text"
                                        placeholder="Введите фамилию и/или имя игрока (минимум 2 символа)"
                                        value={searchQuery}
                                        onChange={handleSearchQueryChange}
                                        onKeyPress={handleKeyPress}
                                        disabled={isSearching}
                                    />
                                    <button 
                                        className="searchButton"
                                        onClick={handleSearch}
                                        disabled={isSearching || searchQuery.trim().length < 2}
                                    >
                                        <FiSearch /> {isSearching ? 'Поиск...' : 'Искать'}
                                    </button>
                                </div>
                                <small style={{color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                                    Введите фамилию или фамилию и имя через пробел (например: Иванов Иван). Автоматический поиск при 4+ символах.
                                </small>
                            </div>

                            <div className="searchResultsSection">
                                {isSearching ? (
                                    <div className="noResults">
                                        Поиск...
                                    </div>
                                ) : hasSearched ? (
                                    <>
                                        {searchResults.length > 0 ? (
                                            <>
                                                <div className="playersList">
                                                    {searchResults.map((player) => (
                                                        <div key={player.id} className="playerResultCard">
                                                            <div className="playerResultPhoto">
                                                                {player.image ? (
                                                                    <img 
                                                                        src={
                                                                            player.image.startsWith('http') 
                                                                                ? player.image 
                                                                                : player.image.startsWith('/') 
                                                                                    ? `https://api.ingame-server.ru${player.image}`
                                                                                    : player.image.startsWith('data:')
                                                                                        ? player.image
                                                                                        : `data:image/jpeg;base64,${player.image}`
                                                                        } 
                                                                        alt={`${player.family} ${player.name}`}
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="noPlayerPhoto">Нет фото</div>
                                                                )}
                                                            </div>
                                                            <div className="playerResultInfo">
                                                                <h4>{player.family} {player.name} {player.father_name}</h4>
                                                                <div className="playerResultDetails">
                                                                    {player.birthday && (
                                                                        <span>
                                                                            {new Date(player.birthday).toLocaleDateString('ru-RU')}
                                                                        </span>
                                                                    )}
                                                                    {player.position && (
                                                                        <span>• {player.position}</span>
                                                                    )}
                                                                    {player.id && (
                                                                        <span className="playerIdBadge">ID: {player.id}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <button
                                                                className="addPlayerButton"
                                                                onClick={() => handleSelectPlayer(player)}
                                                                disabled={isAdding}
                                                            >
                                                                <FiPlus /> Выбрать
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div style={{marginTop: '20px', textAlign: 'center'}}>
                                                    <p style={{color: '#718096', marginBottom: '12px', fontSize: '14px'}}>
                                                        Не нашли нужного игрока?
                                                    </p>
                                                    <button 
                                                        className="createPlayerButton"
                                                        onClick={() => setShowCreatePlayerModal(true)}
                                                    >
                                                        <FiUserPlus /> Создать нового игрока
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="noResults">
                                                <p>Игроки с запросом "{searchQuery}" не найдены</p>
                                                <button 
                                                    className="createPlayerButton"
                                                    onClick={() => setShowCreatePlayerModal(true)}
                                                >
                                                    <FiUserPlus /> Создать нового игрока
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="noResults">
                                        Введите минимум 2 символа и нажмите "Искать" или введите 4+ символа для автоматического поиска
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="playerNumberSection">
                            <button className="backButton" onClick={handleBackToSearch}>
                                ← Вернуться к поиску
                            </button>
                            
                            <div className="selectedPlayerCard">
                                <div className="selectedPlayerPhoto">
                                    {selectedPlayer.image ? (
                                        <img 
                                            src={
                                                selectedPlayer.image.startsWith('http') 
                                                    ? selectedPlayer.image 
                                                    : selectedPlayer.image.startsWith('/') 
                                                        ? `https://api.ingame-server.ru${selectedPlayer.image}`
                                                        : selectedPlayer.image.startsWith('data:')
                                                            ? selectedPlayer.image
                                                            : `data:image/jpeg;base64,${selectedPlayer.image}`
                                            } 
                                            alt={`${selectedPlayer.family} ${selectedPlayer.name}`}
                                            onError={(e) => {
                                                console.error('Failed to load image:', selectedPlayer.image?.substring(0, 100));
                                                e.target.style.display = 'none';
                                            }}
                                            onLoad={() => {
                                                console.log('Image loaded successfully');
                                            }}
                                        />
                                    ) : (
                                        <div className="noPlayerPhoto">Нет фото</div>
                                    )}
                                </div>
                                <div className="selectedPlayerInfo">
                                    <h3>{selectedPlayer.family} {selectedPlayer.name} {selectedPlayer.father_name}</h3>
                                    {selectedPlayer.position && <p>Позиция: {selectedPlayer.position}</p>}
                                </div>
                            </div>

                            <div className="numberInputSection">
                                <label>Игровой номер</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="99"
                                    maxLength={2}
                                    placeholder="Введите номер (1-99)"
                                    value={playerNumber}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        // Ограничиваем ввод двумя цифрами
                                        if (value.length <= 2) {
                                            setPlayerNumber(value);
                                        }
                                    }}
                                    disabled={isAdding}
                                    style={{
                                        borderColor: playerNumber && occupiedNumbers && occupiedNumbers.includes(parseInt(playerNumber)) 
                                            ? '#e53e3e' 
                                            : '#cbd5e0'
                                    }}
                                />
                                {playerNumber && occupiedNumbers && occupiedNumbers.includes(parseInt(playerNumber)) && (
                                    <div style={{
                                        color: '#e53e3e',
                                        fontSize: '13px',
                                        marginTop: '6px',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                    }}>
                                        ⚠️ Номер {playerNumber} уже занят!
                                    </div>
                                )}
                                {occupiedNumbers && occupiedNumbers.length > 0 && (
                                    <small style={{color: '#718096', fontSize: '12px', marginTop: '8px', display: 'block'}}>
                                        Занятые номера: {occupiedNumbers.sort((a, b) => a - b).join(', ')}
                                    </small>
                                )}
                                <button
                                    className="confirmAddButton"
                                    onClick={handleAddPlayer}
                                    disabled={isAdding || !playerNumber || (occupiedNumbers && occupiedNumbers.includes(parseInt(playerNumber)))}
                                >
                                    {isAdding ? 'Добавление...' : 'Дозаявить игрока'}
                                </button>
                            </div>
                        </div>
                    )
                    ) : (
                        // Режим "несколько игроков"
                        <div className="multiplePlayersSection">
                            {!selectedSourceApplication ? (
                                <>
                                    <div className="applicationSelectionSection">
                                        <label>Выберите заявку для копирования игроков</label>
                                        {isLoadingApplications ? (
                                            <div style={{textAlign: 'center', padding: '20px'}}>
                                                Загрузка заявок...
                                            </div>
                                        ) : availableApplications.length > 0 ? (
                                            <div className="applicationsList">
                                                {availableApplications.map(app => (
                                                    <div 
                                                        key={app.application_id}
                                                        className="applicationCard"
                                                        onClick={() => handleSelectSourceApplication(app.application_id)}
                                                    >
                                                        <div className="applicationCardInfo">
                                                            <h4>{app.application_name || `Заявка #${app.application_id}`}</h4>
                                                            <p>{app.data?.length || 0} игроков</p>
                                                        </div>
                                                        <FiPlus />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="noResults">
                                                <p>Нет доступных заявок для копирования</p>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <button className="backButton" onClick={() => setSelectedSourceApplication(null)}>
                                        ← Назад к списку заявок
                                    </button>
                                    
                                    <div className="selectedApplicationInfo">
                                        <h3>{selectedSourceApplication.application_name || `Заявка #${selectedSourceApplication.application_id}`}</h3>
                                        <p>{sourceApplicationPlayers.length} игроков</p>
                                    </div>

                                    {sourceApplicationPlayers.length > 0 && (
                                        <>
                                            <div className="sortSelector" style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '12px',
                                                background: '#f7fafc',
                                                borderRadius: '8px',
                                                marginBottom: '12px'
                                            }}>
                                                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                                    <label style={{fontWeight: '500', color: '#2d3748'}}>Сортировка:</label>
                                                    <select 
                                                        value={sortBy}
                                                        onChange={(e) => setSortBy(e.target.value)}
                                                        style={{
                                                            padding: '6px 12px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #cbd5e0',
                                                            background: 'white',
                                                            cursor: 'pointer',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        <option value="family">По ФИО</option>
                                                        <option value="number">По номеру</option>
                                                    </select>
                                                </div>
                                                <label className="checkboxLabel">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedPlayers.length === sourceApplicationPlayers.length}
                                                        onChange={handleToggleAll}
                                                    />
                                                    <span>Выбрать всех ({sourceApplicationPlayers.length})</span>
                                                </label>
                                            </div>
                                        </>
                                    )}

                                    <div className="playersListForSelection">
                                        {getSortedPlayers(sourceApplicationPlayers).map((player, index) => {
                                            const playerId = player.id || player.player_id;
                                            const isSelected = selectedPlayers.some(p => (p.id || p.player_id) === playerId);
                                            
                                            return (
                                                <div 
                                                    key={index} 
                                                    className={`playerSelectionCard ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => handleTogglePlayer(player)}
                                                >
                                                    <div className="playerSelectionCheckbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => {}}
                                                        />
                                                    </div>
                                                    <div className="playerSelectionPhoto">
                                                        {player.image ? (
                                                            <img 
                                                                src={
                                                                    player.image.startsWith('http') 
                                                                        ? player.image 
                                                                        : player.image.startsWith('/') 
                                                                            ? `https://api.ingame-server.ru${player.image}`
                                                                            : player.image.startsWith('data:')
                                                                                ? player.image
                                                                                : `data:image/jpeg;base64,${player.image}`
                                                                } 
                                                                alt={player.family}
                                                                onError={(e) => { e.target.style.display = 'none'; }}
                                                            />
                                                        ) : (
                                                            <div className="noPlayerPhoto">Нет фото</div>
                                                        )}
                                                    </div>
                                                    <div className="playerSelectionInfo">
                                                        <h4>{player.family} {player.name} {player.father_name}</h4>
                                                        <div className="playerSelectionDetails">
                                                            {player.position && <span>{player.position}</span>}
                                                        </div>
                                                    </div>
                                                    {(player.player_number || player.number) && (
                                                        <div className="playerNumberBadge">
                                                            №{player.player_number || player.number}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {selectedPlayers.length > 0 && (
                                        <div className="multiplePlayersActions">
                                            <p>Выбрано игроков: {selectedPlayers.length}</p>
                                            <button
                                                className="confirmAddButton"
                                                onClick={handleAddMultiplePlayers}
                                                disabled={isAdding}
                                            >
                                                {isAdding ? 'Добавление...' : `Добавить ${selectedPlayers.length} игроков`}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Модальное окно создания нового игрока */}
            <EditPlayerModal
                isOpen={showCreatePlayerModal}
                onClose={() => setShowCreatePlayerModal(false)}
                onSuccess={(newPlayer) => {
                    setShowCreatePlayerModal(false);
                    // После создания игрока автоматически выбираем его
                    handleSelectPlayer(newPlayer);
                }}
                player={null}
                isCreateMode={true}
                userData={user}
            />
        </div>
    );
}
